import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useContent, DEFAULT_CONTENT } from "../context/ContentContext";
import { storage } from "../firebase";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import { Save, LogOut, RefreshCw, ChevronDown, ChevronUp, Upload, Plus, Trash2, Image } from "lucide-react";
import logo from "../assets/logo.png";
import "./Admin.css";

/* ── Image uploader ── */
function ImgUpload({ label, value, path, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try {
      const r = sRef(storage, `${path}/${Date.now()}_${file.name}`);
      await uploadBytes(r, file);
      onDone(await getDownloadURL(r));
      toast.success("Image uploaded!");
    } catch { toast.error("Upload failed. Check Firebase Storage rules."); }
    setBusy(false);
  };
  return (
    <div className="img-upload-row">
      {value && <img src={value} alt="preview" className="img-thumb" />}
      <label className="upload-lbl">
        <input type="file" accept="image/*" onChange={upload} hidden />
        <Upload size={13} /> {busy ? "Uploading…" : (value ? "Replace" : `Upload ${label}`)}
      </label>
    </div>
  );
}

/* ── Accordion ── */
function Acc({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="acc">
      <button className="acc-h" onClick={() => setOpen((o) => !o)}>
        <span className="acc-hl"><span>{icon}</span>{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="acc-body">{children}</div>}
    </div>
  );
}

/* ── Field helpers ── */
const Field = ({ label, children }) => (
  <div className="adm-field"><label>{label}</label>{children}</div>
);
const Row = ({ children }) => <div className="adm-row">{children}</div>;

export default function AdminDashboard() {
  const { user, logout }         = useAuth();
  const { content, saveContent } = useContent();
  const [draft, setDraft]        = useState(JSON.parse(JSON.stringify(content)));
  const [tab,   setTab]          = useState("hero");
  const [saving, setSaving]      = useState(false);
  const navigate                 = useNavigate();

  /* deep set helper */
  const set = (path, val) => {
    const keys = path.split(".");
    setDraft((d) => {
      const c = JSON.parse(JSON.stringify(d));
      keys.reduce((o, k, i) => (i === keys.length - 1 ? (o[k] = val) : o[k]), c);
      return c;
    });
  };

  const setArr = (section, idx, field, val) =>
    setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c[section][idx][field] = val; return c; });

  const save = async () => {
    setSaving(true);
    try { await saveContent(draft); toast.success("Published to everlinktelesat.com!"); }
    catch (e) { toast.error("Save failed: " + e.message); }
    setSaving(false);
  };

  const reset = () => {
    if (window.confirm("Reset all content to defaults?")) {
      setDraft(JSON.parse(JSON.stringify(DEFAULT_CONTENT)));
      toast("Draft reset. Click Save to publish.", { icon: "⚠️" });
    }
  };

  const TABS = [
    { id: "hero",     icon: "🏠", label: "Hero" },
    { id: "about",    icon: "📖", label: "About" },
    { id: "services", icon: "⚡", label: "Services" },
    { id: "projects", icon: "📁", label: "Projects" },
    { id: "team",     icon: "👥", label: "Team" },
    { id: "partners", icon: "🤝", label: "Partners" },
    { id: "contact",  icon: "✉️", label: "Contact" },
  ];

  return (
    <div className="adm-shell">
      <div className="star-bg" /><div className="grid-overlay" />

      {/* Sidebar */}
      <aside className="adm-sb">
        <div className="adm-sb-top">
          <img src={logo} alt="Everlink" className="adm-sb-logo" />
          <div>
            <p className="adm-sb-brand">Everlink Admin</p>
            <p className="adm-sb-email">{user?.email}</p>
            <p className="adm-sb-online">● Online · JWT Verified</p>
          </div>
        </div>
        <nav className="adm-nav">
          {TABS.map((t) => (
            <button key={t.id} className={`adm-nav-item ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}>
              <span>{t.icon}</span>{t.label}
            </button>
          ))}
        </nav>
        <div className="adm-sb-actions">
          <button className="adm-btn-pub" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? "Publishing…" : "Save & Publish"}
          </button>
          <a href="/" target="_blank" rel="noreferrer" className="adm-btn-sm adm-btn-out">
            🌐 View Website
          </a>
          <button className="adm-btn-sm adm-btn-out" onClick={reset}>
            <RefreshCw size={13} /> Reset Defaults
          </button>
          <button className="adm-btn-sm adm-btn-red" onClick={() => { logout(); navigate("/admin"); }}>
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="adm-main">
        <div className="adm-topbar">
          <h1>Content Management</h1>
          <button className="adm-btn-pub" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? "Publishing…" : "Save & Publish"}
          </button>
        </div>

        <div className="adm-body">

          {/* ── HERO ── */}
          {tab === "hero" && (
            <>
              <Acc title="Hero Background Photo" icon="🖼️" defaultOpen>
                <ImgUpload label="Hero Photo" value={draft.hero?.heroImg} path="hero"
                  onDone={(url) => set("hero.heroImg", url)} />
                <p className="adm-hint">Landscape, min 1200×600px. This is the full-bleed photo behind your headline.</p>
              </Acc>
              <Acc title="Hero Text" icon="✍️" defaultOpen>
                <Field label="Eyebrow Tagline"><input value={draft.hero.tagline} onChange={(e) => set("hero.tagline", e.target.value)} /></Field>
                <Field label="Main Headline"><input value={draft.hero.headline} onChange={(e) => set("hero.headline", e.target.value)} /></Field>
                <Field label="Sub-text"><textarea rows={3} value={draft.hero.subtext} onChange={(e) => set("hero.subtext", e.target.value)} /></Field>
                <Row>
                  <Field label="Button 1 Text"><input value={draft.hero.cta1} onChange={(e) => set("hero.cta1", e.target.value)} /></Field>
                  <Field label="Button 2 Text"><input value={draft.hero.cta2} onChange={(e) => set("hero.cta2", e.target.value)} /></Field>
                </Row>
              </Acc>
            </>
          )}

          {/* ── ABOUT ── */}
          {tab === "about" && (
            <Acc title="About Section" icon="📖" defaultOpen>
              <Field label="Paragraph 1"><textarea rows={4} value={draft.about.p1} onChange={(e) => set("about.p1", e.target.value)} /></Field>
              <Field label="Paragraph 2"><textarea rows={4} value={draft.about.p2} onChange={(e) => set("about.p2", e.target.value)} /></Field>
              <Field label="Paragraph 3"><textarea rows={3} value={draft.about.p3} onChange={(e) => set("about.p3", e.target.value)} /></Field>
              <Field label="Vision Statement"><textarea rows={3} value={draft.about.vision} onChange={(e) => set("about.vision", e.target.value)} /></Field>
              <Field label="Mission Statement"><textarea rows={3} value={draft.about.mission} onChange={(e) => set("about.mission", e.target.value)} /></Field>
            </Acc>
          )}

          {/* ── SERVICES ── */}
          {tab === "services" && (
            <Acc title="Solar Mini-Grids Service" icon="⚡" defaultOpen>
              <Field label="Service Title"><input value={draft.service.title} onChange={(e) => set("service.title", e.target.value)} /></Field>
              <Field label="Description"><textarea rows={4} value={draft.service.desc} onChange={(e) => set("service.desc", e.target.value)} /></Field>
              <Row>
                <Field label="Metric 1 Value"><input value={draft.service.m1val} onChange={(e) => set("service.m1val", e.target.value)} /></Field>
                <Field label="Metric 1 Label"><input value={draft.service.m1label} onChange={(e) => set("service.m1label", e.target.value)} /></Field>
              </Row>
              <Row>
                <Field label="Metric 2 Value"><input value={draft.service.m2val} onChange={(e) => set("service.m2val", e.target.value)} /></Field>
                <Field label="Metric 2 Label"><input value={draft.service.m2label} onChange={(e) => set("service.m2label", e.target.value)} /></Field>
              </Row>
              <Row>
                <Field label="Metric 3 Value"><input value={draft.service.m3val} onChange={(e) => set("service.m3val", e.target.value)} /></Field>
                <Field label="Metric 3 Label"><input value={draft.service.m3label} onChange={(e) => set("service.m3label", e.target.value)} /></Field>
              </Row>
              {draft.service.features.map((f, i) => (
                <Row key={i}>
                  <Field label={`Feature ${i+1} Title`}><input value={f.title} onChange={(e) => { const d = JSON.parse(JSON.stringify(draft)); d.service.features[i].title = e.target.value; setDraft(d); }} /></Field>
                  <Field label="Description"><input value={f.desc} onChange={(e) => { const d = JSON.parse(JSON.stringify(draft)); d.service.features[i].desc = e.target.value; setDraft(d); }} /></Field>
                </Row>
              ))}
            </Acc>
          )}

          {/* ── PROJECTS ── */}
          {tab === "projects" && (
            <>
              {draft.projects.map((p, i) => (
                <Acc key={p.id} title={p.title} icon="📁">
                  <Row>
                    <Field label="Title"><input value={p.title} onChange={(e) => setArr("projects", i, "title", e.target.value)} /></Field>
                    <Field label="Capacity"><input value={p.capacity} onChange={(e) => setArr("projects", i, "capacity", e.target.value)} /></Field>
                    <Field label="Year"><input value={p.year} onChange={(e) => setArr("projects", i, "year", e.target.value)} /></Field>
                  </Row>
                  <Field label="Location"><input value={p.location} onChange={(e) => setArr("projects", i, "location", e.target.value)} /></Field>
                  <Field label="Description"><textarea rows={3} value={p.desc} onChange={(e) => setArr("projects", i, "desc", e.target.value)} /></Field>
                  <Field label="Project Photo">
                    <ImgUpload label="Photo" value={p.image} path={`projects/${i}`}
                      onDone={(url) => setArr("projects", i, "image", url)} />
                  </Field>
                </Acc>
              ))}
            </>
          )}

          {/* ── TEAM ── */}
          {tab === "team" && (
            <>
              {draft.team.map((m, i) => (
                <Acc key={m.id} title={m.name} icon="👤">
                  <Row>
                    <Field label="Full Name"><input value={m.name} onChange={(e) => setArr("team", i, "name", e.target.value)} /></Field>
                    <Field label="Role / Title"><input value={m.role} onChange={(e) => setArr("team", i, "role", e.target.value)} /></Field>
                  </Row>
                  <Field label="Biography"><textarea rows={4} value={m.bio} onChange={(e) => setArr("team", i, "bio", e.target.value)} /></Field>
                  <Field label="Profile Photo (portrait, Nokia 3310 size)">
                    <ImgUpload label="Photo" value={m.photo} path={`team/${i}`}
                      onDone={(url) => setArr("team", i, "photo", url)} />
                  </Field>
                </Acc>
              ))}
              <button className="adm-add-btn" onClick={() =>
                setDraft((d) => ({ ...d, team: [...d.team, { id: `t${Date.now()}`, name: "New Member", role: "", bio: "", photo: "" }] }))
              }><Plus size={14} /> Add Team Member</button>
            </>
          )}

          {/* ── PARTNERS ── */}
          {tab === "partners" && (
            <Acc title="International Partners" icon="🤝" defaultOpen>
              {draft.partners.map((p, i) => (
                <Row key={i}>
                  <Field label="Partner Name"><input value={p.name} onChange={(e) => setArr("partners", i, "name", e.target.value)} /></Field>
                  <Field label="Country"><input value={p.country} onChange={(e) => setArr("partners", i, "country", e.target.value)} /></Field>
                  <Field label="Partnership Area"><input value={p.area} onChange={(e) => setArr("partners", i, "area", e.target.value)} /></Field>
                  <button className="adm-del" onClick={() => setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.partners.splice(i, 1); return c; })}><Trash2 size={13} /></button>
                </Row>
              ))}
              <button className="adm-add-btn" onClick={() =>
                setDraft((d) => ({ ...d, partners: [...d.partners, { name: "", country: "", area: "" }] }))
              }><Plus size={14} /> Add Partner</button>
            </Acc>
          )}

          {/* ── CONTACT ── */}
          {tab === "contact" && (
            <Acc title="Contact Details" icon="✉️" defaultOpen>
              <Field label="Email Address"><input value={draft.contact.email} onChange={(e) => set("contact.email", e.target.value)} /></Field>
              <Field label="Contact Form Intro Text"><textarea rows={3} value={draft.contact.formIntro} onChange={(e) => set("contact.formIntro", e.target.value)} /></Field>
              <Field label="Date Incorporated"><input value={draft.contact.incorporated} onChange={(e) => set("contact.incorporated", e.target.value)} /></Field>
            </Acc>
          )}

          <div className="adm-save-bar">
            <p>All changes save to Firebase and go live instantly on <span>everlinktelesat.com</span></p>
            <button className="adm-btn-pub" onClick={save} disabled={saving}>
              <Save size={14} /> {saving ? "Publishing…" : "Save & Publish All"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
